// Phase 6 — native Angular Material replacement for the AngularJS add/edit-details modal
// (sudo/grade-templates/AddGradeTemplateController.js + add-grade-template.html, both DELETED).
//
// One component serves both flows (mirrors the single shared ng1 controller): `data.editing`
// chooses create vs update. `code` is immutable when editing (the legacy `ng-disabled="editing"`),
// so we disable that control; `update` omits `code` exactly like the old controller. On success we
// close with `true` so GradeTemplatesListComponent knows to reload and show a snackbar.
//
// The legacy form had NO validation; we add a light `Validators.required` on `name` (and `code`
// when adding) as a low-risk guard — otherwise behavior is unchanged.
import { Component, inject } from '@angular/core';
import type { OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { GradeTemplateService } from './grade-template.service';

export interface AddGradeTemplateData {
    editing: boolean;
    gradeTemplate?: any;
}

@Component({
    selector: 'add-grade-template',
    standalone: true,
    imports: [ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule],
    templateUrl: './add-grade-template.component.html',
    styleUrl: './add-grade-template.component.css',
})
export class AddGradeTemplateComponent implements OnInit {
    private fb = inject(FormBuilder);
    private service = inject(GradeTemplateService);
    private dialogRef = inject(MatDialogRef<AddGradeTemplateComponent>);
    data = inject<AddGradeTemplateData>(MAT_DIALOG_DATA);

    form = this.fb.group({
        name: this.fb.nonNullable.control('', Validators.required),
        code: this.fb.nonNullable.control('', Validators.required),
        description: this.fb.nonNullable.control(''),
        finalExamBarrier: this.fb.control<number | null>(null),
    });

    ngOnInit(): void {
        if (this.data.editing) {
            const t = this.data.gradeTemplate;
            this.form.patchValue({
                name: t.name,
                code: t.code,
                description: t.description,
                finalExamBarrier: t.finalExamBarrier,
            });
            // `code` is immutable when editing (mirrors ng-disabled="editing"); a disabled
            // control is also excluded from validation, so its `required` won't block here.
            this.form.controls.code.disable();
        }
    }

    submit(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }
        // getRawValue() (not .value) so the `code` control — disabled in edit mode — is still read.
        const { name, code, description, finalExamBarrier } = this.form.getRawValue();

        const done = () => this.dialogRef.close(true);
        if (this.data.editing) {
            this.service.update({ _id: this.data.gradeTemplate._id, name, description, finalExamBarrier }).then(done);
        } else {
            // `?? undefined` so an empty barrier is OMITTED from the payload → the server's Mongoose
            // default (20) applies, matching the legacy form (which sent `undefined`). Sending `null`
            // would store null and bypass the default. `0` is preserved (nullish-only coalescing).
            this.service
                .create({ name, code, description, finalExamBarrier: finalExamBarrier ?? undefined })
                .then(done);
        }
    }

    close(): void {
        this.dialogRef.close();
    }
}
